import React, { useEffect, useMemo, useState } from "react";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import {
  getSettingsError,
  getSettingsStatus,
  getSettingsSuccess,
  updateStatus,
  updateCurrency,
  selectAllSettings,
  getRestaurantCurrencies,
  selectRestaurantCurrencies,
} from "../../../features/settingSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import AppBarSearch from "../../../global/appBarSearch";
import NoDataTable from "../../../components/noDataTable";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import { useTranslation } from "react-i18next";
import TableActions from "../../../components/tableActions";

const SettingsCurrency = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { restaurantId, setCurrency } = useRestaurant();
  const [search, setSearch] = useState("");
  const settings = useSelector(selectAllSettings);
  const currencies = useSelector(selectRestaurantCurrencies);
  const error = useSelector(getSettingsError);
  const success = useSelector(getSettingsSuccess);
  const settingStatus = useSelector(getSettingsStatus);
  const defaultCurrency = settings?.defaultCurrency || "";

  const handleSelect = (currency) => {
    if (!currency?.code || currency.symbol === defaultCurrency) return;
    dispatch(
      updateCurrency({
        body: { defaultCurrency: currency.code },
        restaurantId,
      })
    );
  };

  useEffect(() => {
    dispatch(getRestaurantCurrencies(restaurantId));
  }, [dispatch, restaurantId]);

  useEffect(() => {
    if (settingStatus === "updateSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      dispatch(getRestaurantCurrencies(restaurantId));
    } else if (settingStatus === "updateError") {
      toast.error(error);
      dispatch(updateStatus());
    }
  }, [settingStatus, dispatch, success, error, restaurantId]);

  useEffect(() => {
    if (defaultCurrency) {
      setCurrency(defaultCurrency);
    }
  }, [defaultCurrency, setCurrency]);

  const filteredCurrenciesMemo = useMemo(() => {
    const list = currencies || [];
    if (!search) return list;
    const term = search.toLowerCase();
    return list.filter(
      (currency) =>
        currency.name?.toLowerCase().includes(term) ||
        currency.code?.toLowerCase().includes(term) ||
        currency.symbol?.toLowerCase().includes(term)
    );
  }, [currencies, search]);

  return (
    <div className="main-application">
      <AppBarSearch
        searchText={t("search.byNameCodeSymbol")}
        handleSearch={(e) => setSearch(e.target.value)}
        title={t("pages.currencyManagement")}
      />
      <main>
        <Box sx={{ my: 2 }}>
          <TableContainer>
            <Table sx={{ backgroundColor: colors.primary[400] }}>
              <TableHead sx={{ backgroundColor: colors.primary[700] }}>
                <TableRow>
                  <TableCell>{t("table.name")}</TableCell>
                  <TableCell>{t("table.code")}</TableCell>
                  <TableCell>{t("table.symbol")}</TableCell>
                  <TableCell align="right">{t("table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settingStatus === "loadingCurrencies" ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Loading size={8} />
                    </TableCell>
                  </TableRow>
                ) : settingStatus === "fetchCurrenciesError" ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Error>{error}</Error>
                    </TableCell>
                  </TableRow>
                ) : filteredCurrenciesMemo.length > 0 ? (
                  filteredCurrenciesMemo.map((currency) => {
                    const isDefault = currency.symbol === defaultCurrency;
                    return (
                      <TableRow key={currency.code}>
                        <TableCell>
                          <ParagraphHighliter
                            title={currency.name}
                            search={search}
                          />
                        </TableCell>
                        <TableCell>
                          <ParagraphHighliter
                            title={currency.code}
                            search={search}
                          />
                        </TableCell>
                        <TableCell>
                          <ParagraphHighliter
                            title={currency.symbol}
                            search={search}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isDefault ? (
                            <Chip
                              size="small"
                              color="success"
                              variant="filled"
                              icon={<CheckCircleIcon />}
                              label={t("actions.currentCurrency")}
                            />
                          ) : (
                            <TableActions
                              actions={[
                                {
                                  type: "activate",
                                  color: "success",
                                  label: t("actions.useThisCurrency"),
                                  onClick: () => handleSelect(currency),
                                },
                              ]}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <NoDataTable cols={4} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </main>
    </div>
  );
};

export default SettingsCurrency;
